let isLast = false;
let lastVisitedFocusableElement = null;

/**
 * @private
 * @description
 * Returns an array of all focusable elments in the DOM
 * @param { Object } sourceElement the source element, document by default
 * @param { Boolean } ignoreNegativeTabindex Whenever to ignore elements with `tabindex=-1`
 * @returns { Array } 
 */
const getAllFocusableElements = function(sourceElement = document, ignoreNegativeTabindex = false) {
    if ( arguments.length < 2 && typeof arguments[0] == 'boolean' ) {
        ignoreNegativeTabindex = arguments[0];
        sourceElement = document;
    }

    const keyboardFocusableElements = [...sourceElement.querySelectorAll('a[href], button, details, fieldset, input, textarea, select, [tabindex], .ag-virtual-list-item')];
    return keyboardFocusableElements.filter((element) => {
        if ( element.classList.contains('ag-virtual-list-item') && element.tabIndex === -1 ) return true; // AgGrid Default Popup list exception
        if ( ignoreNegativeTabindex ) return !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden') && !element.classList.contains('ag-tab-guard');
        else return !element.hasAttribute('disabled') && !element.getAttribute('aria-hidden') && !element.classList.contains('ag-tab-guard') && element.tabIndex !== -1;
    });
}

/**
 * @private
 * @description
 * Returns an array of focusable elements with a given attribute the DOM
 * @param { String } attrValue the target attribute
 * @param { Object } options additonal options
 * @returns { Array } 
 */
const getFocusableElementsByAttrValue = function(attrValue, options = {}) {
    const focusableElements = getAllFocusableElements(options.element, options.ignoreIndex);
    return focusableElements.filter((element) => element.getAttribute('cs-keyboard') === attrValue);
}

/**
 * @private
 * @description
 * Returns the next focsuable element
 * @param { Object } element a focused element, a current active element by default
 * @returns { Object } 
 */
const getNextFocusableElement = function(element = document.activeElement) {
    const focusableElements = getAllFocusableElements();
    const currentElementIndex = focusableElements.indexOf(element);
    return focusableElements[currentElementIndex + 1];
}

/**
 * @private
 * @description
 * Returns the previous focsuable element
 * @param { Object } element a focused element, a current active element by default
 * @returns { Object } 
 */
const getPreviousFocusableElement = function(element = document.activeElement) {
    const focusableElements = getAllFocusableElements();
    const currentElementIndex = focusableElements.indexOf(element);
    return focusableElements[currentElementIndex - 1];
}

/**
 * @private
 * @param { Object } element a focusable element
 * @param { Object } event a keyboard event
 * @description
 * Set focus on the given element or the next available. 
 * If no focusable elements it sets focus on the first available element. 
 * @returns void
 */
const focusOnNextElement = function(element, event) {
    if ( element != undefined ) element.focus();
    else {
        const nextFocusableElement = getNextFocusableElement();
        if ( nextFocusableElement ) nextFocusableElement.focus();
        else {
            if ( event != undefined ) event.preventDefault();
            const focusableElements = getAllFocusableElements();
            const firstFocusableElement = focusableElements[0];
            if ( firstFocusableElement ) firstFocusableElement.focus();
        }
    }
}

/**
 * @private
 * @description
 * Set focus on the given element or the previously available. 
 * If no focusable elements it sets focus on the first available element. 
 * @param { Object } element a focusable element
 * @param { Object } event a keyboard event
 * @returns void
 */
const focusOnPreviousElement = function(element, event) {
    if ( element != undefined ) element.focus();
    else {
        const previousFocusableElement = getPreviousFocusableElement();
        if ( previousFocusableElement ) previousFocusableElement.focus();
        else { 
            if ( event != undefined ) event.preventDefault();
            const focusableElements = getAllFocusableElements();
            const lastFocusableElement = focusableElements[focusableElements.length - 1];
            if ( lastFocusableElement ) lastFocusableElement.focus();
        }
    }
}

/**
 * @private
 * @description
 * Helps shifting a focus on the next element with the support for the native Tab event
 * @param { Array } list the array of elements
 * @param { Number } index the index of the element
 * @param { String } direction the direction for the search
 * @param { Object } nativeEvent the user keyboard event
 * @returns void
 */
const shiftFocusSuportNative = function(list, index, direction, nativeEvent) {
    switch ( direction ) {
        case 'forward':
            if ( index == list.length - 1 ) {
                focusOnNextElement(null, nativeEvent);

                if ( isLast ) isLast = false; 
                else isLast = true;
            }
            break;
        case 'backward':
            if ( index < list.length - 1 ) isLast = false;
            break;
    }
}

/**
 * @private
 * @description
 * Helps shifting a focus on the next element ignoring a native Tab events. 
 * @param { Array } list the array of elements
 * @param { Number } index the index of the element
 * @param { String } direction the direction for the search
 * @param { Object } nativeEvent the user keyboard event
 * @param { Boolean } insideLoop whenever focus on elements in the same group
 * @returns void
 */
const shiftFocusManually = function(event, element, list, index, direction, insideLoop) {
    // We're disabling native event as we're going to handle it manually from that point
    event.preventDefault();

    if ( lastVisitedFocusableElement == undefined ) lastVisitedFocusableElement = element;
    switch ( direction ) {
        case 'forward':
            if ( list[index + 1] == undefined ) {
                if ( insideLoop ) {
                    focusOnNextElement(list[0]);
                }
                else if ( lastVisitedFocusableElement ) {
                    const focusableElements = getAllFocusableElements();
                    const lastFocusableElementIndex = focusableElements.indexOf(lastVisitedFocusableElement);
                    if ( lastFocusableElementIndex != -1 ) {
                        focusOnNextElement(focusableElements[lastFocusableElementIndex + 1]);
                    }
                }

                lastVisitedFocusableElement = null;

                break;
            }
            else {
                focusOnNextElement(list[index + 1]);
                break;
            }
        case 'backward':
            if ( list[index - 1] == undefined ) {
                if ( insideLoop ) {
                    focusOnPreviousElement(list[list.length - 1])
                }
                else if ( lastVisitedFocusableElement ) {
                    const focusableElements = getAllFocusableElements();
                    const lastFocusableElementIndex = focusableElements.indexOf(lastVisitedFocusableElement);
                    if ( lastFocusableElementIndex != -1 ) {
                        focusOnPreviousElement(focusableElements[lastFocusableElementIndex - 1]);
                    }   
                }

                lastVisitedFocusableElement = null;
                
                break;
            }
            else {
                focusOnPreviousElement(list[index - 1]);
                break;
            }
    }
}

export default {
    install: function(Vue) {
        /**
         * @public
         * @description
         * Returns all focusable elements in the DOM
         * @returns { Array } 
         */
        Vue.prototype.$getAllFocusableElements = function() {
            return getAllFocusableElements();
        }

        /**
         * @public
         * @description
         * Returns all focusable elments inside source element
         * @param { Object } source 
         * @returns { Array }
         */
        Vue.prototype.$getAllFocusableElementsFrom = function(source) {
            return getAllFocusableElements(source);
        }

        /**
         * @public
         * @description
         * Returns focusable elements with a given attribute
         * @param { String } attrValue 
         * @param { Object } parentElement 
         * @returns { Array }
         */
        Vue.prototype.$getFocusableElementsByAttrValue = function(attrValue, parentElement) {
            return getFocusableElementsByAttrValue(attrValue, { element: parentElement });
        }

        /**
         * @public
         * @description
         * Returns a next focusable element
         * @param { Object } element 
         * @returns { Object }
         */
        Vue.prototype.$getNextFocusableElement = function(element = document.activeElement) {
            return getNextFocusableElement(element);
        }

        /**
         * @public
         * @description
         * Returns a previous focusable element
         * @param { Object } element 
         * @returns { Object }
         */
        Vue.prototype.$getPreviousFocusableElement = function(element = document.activeElement) {
            return getPreviousFocusableElement(element);
        }

        /**
         * @public
         * @description
         * Handles a focus based on the passed arguments like current element or type and manage native or manual shifting. 
         * @param { Object } currentElement 
         * @param { Object } currentEvent 
         * @param { String } currentType 
         * @param { String } controlledType 
         * @param { Boolean } keepInLoop
         * @returns void
         */
        Vue.prototype.$focus = function(currentElement, currentEvent, currentType, controlledType, keepInLoop) {
            const list = controlledType ? getFocusableElementsByAttrValue(controlledType, { ignoreIndex: true }) : currentType ? getFocusableElementsByAttrValue(currentType) : getAllFocusableElements();
            
            // DRAGGABLE CONTROLLER START
            const draggableItems = list.filter((element) => element.hasAttribute('cs-keyboard-draggable'));            
            const draggableItemsChildrenMap = new Map();
            let draggableItemsIndexMap = [];

            if ( draggableItems.length > 0 ) {
                for ( let [index, item] of draggableItems.entries() ) {
                    const itemFocusableChildren = getAllFocusableElements(item);

                    item.setAttribute('cs-key-dr-index', index);
                    if ( !item.hasAttribute('cs-dr-order') ) item.setAttribute('cs-dr-order', index.toString());

                    if ( list.indexOf(item) != -1 ) {
                        list.splice(list.indexOf(item) + 1, itemFocusableChildren.length);
                        draggableItemsChildrenMap.set(item.getAttribute('cs-dr-order'), itemFocusableChildren);
                    }
                }

                draggableItems.forEach((dI) => {
                    if ( list.indexOf(dI) != -1 ) draggableItemsIndexMap.push(list.indexOf(dI))
                });

                while ( draggableItems.length > 0 ) {
                    const item = draggableItems.pop();

                    if ( list.indexOf(item) != -1 ) {
                        list.splice(list.indexOf(item), 1);
                        list.splice(draggableItemsIndexMap[item.getAttribute('cs-dr-order')], 0, item);
                    }
                }

                let lastKnownItemIndex = 0;

                for ( let [index, key] of draggableItemsIndexMap.entries() ) {
                    if ( key === -1 || key == undefined ) continue;
                    if ( !lastKnownItemIndex ) lastKnownItemIndex = key;

                    const draggableItemChildren = Array.from(draggableItemsChildrenMap.get(index.toString()));

                    list.splice(lastKnownItemIndex + index + 1 , 0, ...draggableItemChildren);
                    
                    lastKnownItemIndex = lastKnownItemIndex + draggableItemChildren.length;
                }
            }
            // DRAGGABLE CONTROLLER END

            const index = list.indexOf(currentElement);
            const direction = currentEvent.shiftKey ? 'backward' : 'forward';

            if ( controlledType || currentElement.hasAttribute('cs-key-dr-index') && currentElement.getAttribute('cs-key-dr-index') <= draggableItemsIndexMap.length ||  list[list.indexOf(currentElement) + 1].hasAttribute('cs-key-dr-index') && list[list.indexOf(currentElement) + 1].getAttribute('cs-key-dr-index') <= draggableItemsIndexMap.length ) shiftFocusManually (currentEvent, currentElement, list, index, direction, keepInLoop);
            else shiftFocusSuportNative(list, index, direction, currentEvent);

            draggableItemsIndexMap = [];
            draggableItemsChildrenMap.clear();
        }

        /**
         * @public
         * @description
         * Handles a focus on the first element inside given element
         * @param { Object } element 
         * @returns void
         */
        Vue.prototype.$focusInside = function(element) {
            if ( element == undefined ) return;
            const focusableElements = this.$getAllFocusableElementsFrom(element);
            if ( focusableElements ) focusableElements[0].focus();
        }
    }
}